import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useOutletContext, useLocation } from "react-router-dom";
import { getRealtimeDatabase } from "src/utils/firebase";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Stack,
  useTheme
} from "@mui/material";

import ItarraLogo from "src/assets/images/itarra.svg";

const DemoInfo = () => {
  const { uid, type } = useParams();
  const navigate = useNavigate();
  const db = getRealtimeDatabase();
  const theme = useTheme();
  const location = useLocation();

  const demoText =
    type === "selfie"
      ? "Selfie Only"
      : type === "tractor"
      ? "Tractor Demo"
      : "Just Info";

  const setHideSettings = useOutletContext();
  useEffect(() => {
    if (setHideSettings) setHideSettings(true);
    return () => {
      if (setHideSettings) setHideSettings(false);
    };
  }, [setHideSettings]);

  const [user, setUser] = useState({
    name: "",
    email: "",
    phone: "",
    followup: type === "just_info" ? "followup" : "demo",
    comments: "",
    photo: "",
    emailSent: false,
    photoUrl: "",
    snap: false
  });

  const [errors, setErrors] = useState({});
  const [validSession, setValidSession] = useState(true);

  useEffect(() => {
    if (!db || !uid) return () => {};

    if (!location.pathname.startsWith("/demo_info")) return () => {};

    const documentToRead =
      type === "selfie"
        ? "demo_current_selfie"
        : type === "tractor"
        ? "demo_current_tractor"
        : "demo_current_just_info";

    const refToWatch = db.ref(documentToRead);

    const checkSession = (snapshot) => {
      const current = snapshot.val();
      if (!current) return;
      if (current.uid !== uid) {
        if (documentToRead !== "demo_current_just_info")
        {
          setValidSession(false);
          navigate("/demo_complete/" + uid + "/stopped_at_info");
        }

      }
    };

    refToWatch.on("value", checkSession);
    return () => refToWatch.off("value", checkSession);
  }, [db, uid, navigate, type, location.pathname]);

  const handleChange = (e) => {
    setUser({ ...user, [e.target.name]: e.target.value });
  };

  const validate = () => {
    const newErrors = {};
    if (!user.name.trim()) newErrors.name = "Name is required";
    if (!user.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(user.email)) {
      newErrors.email = "Enter a valid email address";
    }
    return newErrors;
  };

  const handleSubmit = () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    db.ref(`demo_users/${type}/${uid}`).update({
      ...user,
      type: type || "default",
      done: false,
      updatedAt: new Date().toISOString()
    });

    if (type === "selfie") {
      navigate(`/demo/selfie_userpage/${uid}`);
    } else if (type === "just_info") {
      navigate(`/demo_complete/${uid}/just_info`);
    } else {
      navigate(`/demo_connect/${uid}`);
    }
  };

  if (!validSession) return null;

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      height="100vh"
      width="100vw"
      sx={{
        backgroundColor: theme.palette.background.default,
        p: 1,
        boxSizing: "border-box"
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, sm: 3 },
          width: { xs: "95vw", sm: "85vw", md: "70vw", lg: "50vw" },
          height: "100%",
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box"
        }}
      >
        <Box sx={{ mb: 2, textAlign: "center" }}>
          <img
            src={ItarraLogo}
            alt="iTarra Logo"
            style={{ maxHeight: "12vh", maxWidth: "60%" }}
          />
        </Box>

        <Box flex={1} display="flex" flexDirection="column" overflow="auto">
          <Typography variant="h5" gutterBottom align="center">
            Enter Your Information
          </Typography>

          <TextField
            fullWidth
            margin="dense"
            label="Name"
            name="name"
            value={user.name}
            onChange={handleChange}
            error={!!errors.name}
            helperText={errors.name}
          />

          <TextField
            fullWidth
            margin="dense"
            label="Email"
            name="email"
            value={user.email}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email}
          />

          <TextField
            fullWidth
            margin="dense"
            label="Phone Number (optional)"
            name="phone"
            value={user.phone}
            onChange={handleChange}
          />

          {/* ✅ Only show followup buttons if not just_info */}
          {type !== "just_info" && (
            <>
              <Typography variant="subtitle1" sx={{ mt: 1, mb: 1 }}>
                Follow-up preference
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <Button
                  fullWidth
                  variant={user.followup === "demo" ? "contained" : "outlined"}
                  color="warning"
                  onClick={() => setUser({ ...user, followup: "demo" })}
                >
                  {demoText}
                </Button>
                <Button
                  fullWidth
                  variant={
                    user.followup === "followup" ? "contained" : "outlined"
                  }
                  color="success"
                  onClick={() => setUser({ ...user, followup: "followup" })}
                >
                  Please follow up with me
                </Button>
              </Stack>
            </>
          )}

          <Box flex={1} mt={1} mb={1} display="flex">
            <TextField
              fullWidth
              label="Comments"
              name="comments"
              value={user.comments}
              onChange={handleChange}
              multiline
              sx={{
                flex: 1,
                "& .MuiInputBase-root": {
                  height: "100%",
                  alignItems: "flex-start"
                }
              }}
            />
          </Box>
        </Box>

        <Button
          variant="contained"
          color="primary"
          fullWidth
          sx={{ mt: 1 }}
          onClick={handleSubmit}
        >
          Next
        </Button>
      </Paper>
    </Box>
  );
};

export default DemoInfo;
